/**
 * HMS General Notification Modal
 * 
 * Reusable modal component for displaying any type of notification with dynamic actions.
 * Follows HMS UI design system (Swiss spa aesthetic).
 * 
 * Features:
 * - Generic structure for all notification types
 * - Special handling for leave requests (approve/reject with reason)
 * - Extensible action system for future notification types
 * - HMS branded styling with gradient header
 * - Fully responsive (desktop, tablet, mobile)
 * - Keyboard accessible (Escape key to close)
 * 
 * Usage Context:
 * - Can be used with REAL notifications (from notifications screen) 
 * - Can be used with SYNTHETIC notifications (converted leave requests from admin dashboard)
 * - Automatically detects synthetic notifications via is_synthetic flag and skips mark-as-read calls
 * 
 * @component
 * @example
 * <HMSNotificationModal
 *   isOpen={true}
 *   notification={notificationObject}
 *   onClose={() => console.log('closed')}
 *   onActionComplete={() => console.log('action completed')}
 * />
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCheck, 
  faExclamationTriangle,
  faUserPlus,
  faSearch,
  faEye,
  faTruck,
  faShip,
  faArrowRight,
  faBoxOpen,
  faCogs,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import notificationService from '../../services/notificationService';
import leaveService from '../../services/leaveService';
import { downloadSickNote } from '../../services/docuwareService';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import { getAbsoluteMediaUrl } from '../../utils/mediaUtils';
import { AuthContext } from '../../context/AuthContext';
import CRMRFCNotificationModal from '../CRM/CRMRFCNotificationModal';
import './HMSNotificationModal.css';
import './HMSNotificationModal-TodoExtension.css';
import { useHistory } from 'react-router-dom';

/**
 * HMSNotificationModal Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Object} props.notification - Full notification object from API
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} [props.onActionComplete] - Optional callback after action taken
 */
function HMSNotificationModal({ isOpen, notification, onClose, onActionComplete }) {
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [showDenyReason, setShowDenyReason] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [leaveRequestStatus, setLeaveRequestStatus] = useState(null);
  const [leaveRequestAdminNotes, setLeaveRequestAdminNotes] = useState(null);
  const [freshMetadata, setFreshMetadata] = useState(null);
  const [freshActionRequired, setFreshActionRequired] = useState(null);
  const [loadingFreshData, setLoadingFreshData] = useState(false);
  const [workAssignment, setWorkAssignment] = useState(null);
  
  // Work assignment state
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);
  
  // Users on leave state
  const [usersOnLeave, setUsersOnLeave] = useState([]);
  const [loadingUsersOnLeave, setLoadingUsersOnLeave] = useState(false);
  
  // Sick note download state
  const [downloadingSickNote, setDownloadingSickNote] = useState(false);

  /**
   * Clean financial notification description by removing redundant metadata
   * that's already displayed in the structured metadata section
   */
  const cleanFinancialDescription = (description) => {
    if (!description) return '';
    
    // Split description into lines
    const lines = description.split('\n');
    
    // Filter out lines that contain metadata already shown in labels
    const filteredLines = lines.filter(line => {
      const trimmedLine = line.trim();
      // Remove lines that start with metadata labels
      return !(
        trimmedLine.startsWith('Amount:') ||
        trimmedLine.startsWith('Approval Notes:') ||
        trimmedLine.startsWith('Approver Notes:') ||
        trimmedLine.startsWith('Rejection Reason:') ||
        trimmedLine.startsWith('Invoice Date:') ||
        trimmedLine.startsWith('Submitted by:') ||
        trimmedLine.startsWith('Approved by:') ||
        trimmedLine.startsWith('Rejected by:') ||
        trimmedLine === 'None' ||
        trimmedLine === ''
      );
    });
    
    // Join the remaining lines and clean up extra whitespace
    return filteredLines.join('\n').trim();
  };

  /**
   * Handle Escape key press to close modal
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  /**
   * Fetch fresh notification data and leave request status when modal opens
   */
  useEffect(() => {
    const fetchNotificationAndLeaveStatus = async () => {
      if (isOpen && notification?.notification_type === 'leave_request' && notification?.metadata?.leave_request_id) {
        setLoadingFreshData(true);
        
        console.log('🔍 Modal opened with notification:', {
          id: notification.id,
          is_synthetic: notification.is_synthetic,
          metadata_cancelled: notification.metadata?.cancelled,
          action_required: notification.action_required
        });
        
        try {
          // Fetch fresh notification data to check for cancellations or updates
          // Only do this if it's a real notification (not synthetic from admin dashboard)
          if (!notification.is_synthetic && notification.id) {
            console.log('📡 Fetching fresh notification data from API...');
            try {
              const notifResponse = await axiosInstance.get(`/api/notifications/${notification.id}/`);
              const freshNotification = notifResponse.data;
              
              console.log('✅ Fresh notification data received:', {
                cancelled: freshNotification.metadata?.cancelled,
                action_required: freshNotification.action_required,
                full_metadata: freshNotification.metadata
              });
              
              // Store fresh metadata and action_required in state
              if (freshNotification.metadata) {
                setFreshMetadata(freshNotification.metadata);
              }
              setFreshActionRequired(freshNotification.action_required);
            } catch (notifErr) {
              console.error('❌ Could not fetch fresh notification data:', notifErr);
              // Continue with existing notification data
              setFreshMetadata(null);
              setFreshActionRequired(null);
            }
          } else {
            console.log('⚠️ Skipping fresh data fetch:', {
              reason: notification.is_synthetic ? 'Synthetic notification (admin dashboard)' : 'No notification ID'
            });
          }
          
          // Use admin endpoint to fetch leave request details
          // Regular endpoint only allows users to see their own requests
          const response = await axiosInstance.get(`/api/leave/admin/requests/?employee=${notification.metadata.employee_id || ''}`);
          
          // Find the specific leave request from the list
          const leaveRequest = response.data.find(lr => lr.id === notification.metadata.leave_request_id);
          
          if (leaveRequest) {
            setLeaveRequestStatus(leaveRequest.status);
            setLeaveRequestAdminNotes(leaveRequest.admin_notes || null);
            
            // Fetch work assignment if leave is approved or completed
            if (['approved', 'completed'].includes(leaveRequest.status)) {
              try {
                const workAssignmentResponse = await axiosInstance.get(
                  `/api/leave/requests/${notification.metadata.leave_request_id}/work-assignment/`
                );
                setWorkAssignment(workAssignmentResponse.data);
                console.log('Work assignment fetched:', workAssignmentResponse.data);
              } catch (workAssignErr) {
                // Work assignment might not exist yet, that's okay
                console.log('No work assignment found or error fetching:', workAssignErr);
                setWorkAssignment(null);
              }
            }
          } else {
            // If not found in list, assume it might still be actionable
            setLeaveRequestStatus(null);
            setLeaveRequestAdminNotes(null);
            setWorkAssignment(null);
          }
        } catch (err) {
          console.error('Error fetching leave request status:', err);
          // If we can't fetch the status, assume it might still be actionable
          setLeaveRequestStatus(null);
          setLeaveRequestAdminNotes(null);
          setWorkAssignment(null);
        } finally {
          setLoadingFreshData(false);
        }
      } else {
        // Not a leave request, no need to fetch
        setLoadingFreshData(false);
      }
    };

    fetchNotificationAndLeaveStatus();
  }, [isOpen, notification]);

  /**
   * Reset state when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setShowDenyReason(false);
      setDenyReason('');
      setLoading(false);
      setLeaveRequestStatus(null);
      setLeaveRequestAdminNotes(null);
      setFreshMetadata(null);
      setFreshActionRequired(null);
      setLoadingFreshData(false);
      setWorkAssignment(null);
    }
  }, [isOpen]);

  /**
   * Check if assignment UI should be displayed for leave requests
   * Assignment is shown for both approval-required AND notify-only leaves
   * UNLESS work has already been assigned
   * 
   * SECURITY: Only show assignment UI to supervisors/HODs, NOT to the employee who requested leave
   */
  const shouldShowAssignment = () => {
    console.log('🔍 shouldShowAssignment CHECK:', {
      notification: notification,
      loadingFreshData: loadingFreshData,
      user: user,
      leaveRequestStatus: leaveRequestStatus
    });
    
    if (!notification || notification.notification_type !== 'leave_request') {
      console.log('🚫 Hiding assignment UI - not a leave request notification');
      return false;
    }
    if (loadingFreshData) {
      console.log('🚫 Hiding assignment UI - still loading fresh data');
      return false;
    }
    if (notification.status === 'archived') {
      console.log('🚫 Hiding assignment UI - notification is archived');
      return false;
    }
    
    const metadata = freshMetadata || notification.metadata;
    console.log('📋 Metadata for assignment check:', metadata);
    
    // CRITICAL: Don't show assignment UI if this is an approval/rejection notification
    // Approval/rejection notifications are sent TO the employee (not supervisors)
    // They contain 'approval_action' in metadata ('approved' or 'rejected')
    if (metadata?.approval_action) {
      console.log('🚫 Hiding assignment UI - this is an approval/rejection notification for the employee');
      return false;
    }
    
    // CRITICAL: Don't show assignment UI to the employee who requested the leave
    // This should only be shown to their supervisor/HOD
    // EXCEPTION: In admin dashboard (synthetic notifications), allow admins to see assignment
    // even for their own leave requests since they're acting in admin capacity
    console.log('👤 User ID comparison:', {
      currentUserId: user?.id,
      requestingEmployeeId: metadata?.employee_id,
      matches: user?.id === metadata?.employee_id,
      isSynthetic: notification?.is_synthetic,
      userRoles: {
        isStaff: user?.is_staff,
        isSupervisor: user?.is_supervisor,
        isHOD: user?.is_head_of_department
      }
    });
    
    // If not a synthetic (admin dashboard) notification, hide assignment UI for own leave
    // For regular notifications (bell dropdown), employees can't see assignment UI for their own leave
    if (user && metadata?.employee_id && user.id === metadata.employee_id && !notification?.is_synthetic) {
      console.log('🚫 Hiding assignment UI - current user is the leave requester (non-admin view)');
      return false;
    }
    
    // For synthetic notifications (admin dashboard), allow viewing assignment for own leave
    // The admin dashboard is already protected by backend auth and route guards
    // If someone can access the admin dashboard, they have admin/supervisor permissions
    if (notification?.is_synthetic && user && metadata?.employee_id && user.id === metadata.employee_id) {
      console.log('✅ Admin dashboard - showing assignment UI even for own leave');
      // Continue to show assignment UI (don't return false)
    }
    
    // Don't show if work assignment already exists
    if (metadata?.work_assignment_exists === true) {
      console.log('🚫 Hiding assignment UI - work assignment already exists');
      return false;
    }
    
    // Don't show for Home Office (HO) or Travel Leave (TL) - employees are still working
    if (metadata?.leave_type_code === 'HO' || metadata?.leave_type_code === 'TL') {
      console.log('🚫 Hiding assignment UI - Home Office/Travel Leave does not require work assignment');
      return false;
    }
    
    // Don't show if request was cancelled
    if (metadata?.cancelled === true) {
      console.log('🚫 Hiding assignment UI - request was cancelled');
      return false;
    }
    
    // Don't show if notification has been superseded (e.g., leave edited)
    if (metadata?.superseded === true) {
      console.log('🚫 Hiding assignment UI - notification was superseded');
      return false;
    }
    
    // Don't show if leave status is rejected or completed
    if (leaveRequestStatus && ['rejected', 'completed', 'cancelled'].includes(leaveRequestStatus)) {
      console.log('🚫 Hiding assignment UI - leave status is', leaveRequestStatus);
      return false;
    }
    
    // Show assignment for both pending (requires approval) AND approved (notify-only) leaves
    // At this point, we know the current user is NOT the requester, so they must be a supervisor/HOD
    console.log('✅ Showing assignment UI - all checks passed');
    return true;
  };

  /**
   * Handle outside clicks for user dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Fetch available users for work assignment when modal opens for leave requests
   */
  useEffect(() => {
    const fetchAvailableUsers = async () => {
      if (isOpen && shouldShowAssignment()) {
        setLoadingUsers(true);
        setUsersError('');
        
        try {
          // Extract leave request dates and employee info from metadata
          const metadata = freshMetadata || notification.metadata;
          const startDate = metadata?.start_date || null;
          const endDate = metadata?.end_date || null;
          const requestingEmployeeId = metadata?.employee_id || null;
          
          console.log('DEBUG: Fetching available users with dates:', {
            metadata: metadata,
            startDate: startDate,
            endDate: endDate,
            requestingEmployeeId: requestingEmployeeId,
            notification: notification
          });
          
          // Pass dates and requesting employee ID to filter appropriately
          // - Exclude users with overlapping approved leave
          // - Exclude the requesting employee (can't assign to themselves)
          // - Include users with pending leave (with badge)
          const users = await leaveService.getAvailableUsers(startDate, endDate, requestingEmployeeId);
          setAvailableUsers(users);
          
          console.log('DEBUG: Available users response:', users);
          
          if (startDate && endDate) {
            console.log(`Fetched ${users.length} available users for leave period ${startDate} to ${endDate}, excluding employee ID ${requestingEmployeeId}`);
          } else {
            console.log(`Fetched ${users.length} available users for work assignment (NO DATES PROVIDED)`);
          }
        } catch (error) {
          console.error('Error fetching available users:', error);
          setUsersError('Unable to load available users');
        } finally {
          setLoadingUsers(false);
        }
      }
    };
    
    fetchAvailableUsers();
  }, [isOpen, notification, loadingFreshData, freshMetadata]);

  /**
   * Fetch users on leave during the leave request period when modal opens
   */
  useEffect(() => {
    const fetchUsersOnLeave = async () => {
      if (isOpen && notification?.notification_type === 'leave_request' && shouldShowAssignment()) {
        setLoadingUsersOnLeave(true);
        
        try {
          // Extract leave request dates and employee info from metadata
          const metadata = freshMetadata || notification.metadata;
          const startDate = metadata?.start_date || null;
          const endDate = metadata?.end_date || null;
          const requestingEmployeeId = metadata?.employee_id || null;
          
          if (startDate && endDate) {
            console.log('Fetching users on leave for period:', startDate, 'to', endDate, 'excluding employee ID:', requestingEmployeeId);
            // Pass requesting employee ID to exclude them from the list
            const users = await leaveService.getUsersOnLeave(startDate, endDate, requestingEmployeeId);
            setUsersOnLeave(users);
            console.log('Users on leave fetched:', users);
          } else {
            console.log('No dates available to fetch users on leave');
            setUsersOnLeave([]);
          }
        } catch (error) {
          console.error('Error fetching users on leave:', error);
          setUsersOnLeave([]);
        } finally {
          setLoadingUsersOnLeave(false);
        }
      } else {
        setUsersOnLeave([]);
        setLoadingUsersOnLeave(false);
      }
    };
    
    fetchUsersOnLeave();
  }, [isOpen, notification, loadingFreshData, freshMetadata]);

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!loading) {
      // Reset work assignment state
      setSelectedUserId(null);
      setSelectedUserName('');
      setUserSearchTerm('');
      setShowUserDropdown(false);
      setAvailableUsers([]);
      setUsersError('');
      setShowDenyReason(false);
      setDenyReason('');
      
      onClose();
    }
  };

  /**
   * Handle overlay click (close modal)
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get a more descriptive notification type display
   * For leave requests, use the title since it's more specific
   */
  const getNotificationTypeDisplay = () => {
    if (notification.notification_type === 'leave_request') {
      // Use the title for leave requests as it's more descriptive
      // e.g., "Leave Request Approved", "Leave Request Rejected", "Leave Request Cancelled"
      return notification.title;
    }
    // For other notification types, use the standard display
    return notification.notification_type_display;
  };

  /**
   * Check if actions should be displayed
   * Actions are hidden if:
   * - action_required is false
   * - notification is archived
   * - notification has been superseded (e.g., leave request was edited)
   * - For leave requests: leave request status is not 'pending'
   * - For leave requests: requires_approval is false (auto-approved types)
   * - For leave requests: request has been cancelled
   * - Currently loading fresh data (prevents brief flash of buttons)
   */
  const shouldShowActions = () => {
    if (!notification) return false;
    
    // Hide actions while loading fresh data to prevent flash
    if (loadingFreshData) return false;
    
    // Use fresh action_required if available, otherwise use notification's value
    const actionRequired = freshActionRequired !== null ? freshActionRequired : notification.action_required;
    if (!actionRequired) return false;
    
    if (notification.status === 'archived') return false;
    
    // Use fresh metadata if available, otherwise use notification's metadata
    const metadata = freshMetadata || notification.metadata;
    
    // If notification has been superseded (e.g., leave edited), hide actions
    if (metadata?.superseded === true) {
      return false;
    }
    
    // For leave requests, check the actual leave request status
    if (notification.notification_type === 'leave_request') {
      // If leave request was cancelled by employee, hide actions
      if (metadata?.cancelled === true) {
        return false;
      }
      
      // If leave type doesn't require approval (auto-approved), hide actions
      if (metadata?.requires_approval === false) {
        return false;
      }
      
      // If we've fetched the status and it's not pending, hide actions
      if (leaveRequestStatus && leaveRequestStatus !== 'pending') {
        return false;
      }
    }
    
    return true;
  };

  /**
   * Handle Leave Request Approval
   */
  const handleApproveLeave = async () => {
    const metadata = freshMetadata || notification.metadata;
    const leaveTypeCode = metadata?.leave_type_code;
    const isHoOrTl = leaveTypeCode === 'HO' || leaveTypeCode === 'TL';
    
    if (!isHoOrTl && !selectedUserId) {
      let daysUntilStart = 0;
      if (metadata?.start_date) {
        const [y, m, d] = metadata.start_date.split('-').map(Number);
        const startDate = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        daysUntilStart = Math.floor((startDate - today) / (1000 * 60 * 60 * 24));
      }
      
      if (daysUntilStart < 2) {
        showError('Leave starts within 2 days. You must assign a replacement now.');
        return;
      }
      
      if (!window.confirm('No replacement assigned. A reminder will be sent 2 days before leave starts. Continue?')) {
        return;
      }
    }
    
    setLoading(true);
    try {
      const leaveRequestId = notification.metadata?.leave_request_id;
      if (!leaveRequestId) {
        showError('Leave request ID not found');
        return;
      }

      await leaveService.approveLeaveRequest(leaveRequestId, selectedUserId, '');
      
      if (!notification.is_synthetic) {
        await notificationService.markAsRead(notification.id);
      }
      
      let successMsg;
      if (isHoOrTl) {
        successMsg = 'Leave request approved successfully';
      } else if (selectedUserId) {
        successMsg = 'Leave request approved and work assigned successfully';
      } else {
        successMsg = 'Leave request approved. Reminder will be sent to assign replacement.';
      }
      showSuccess(successMsg);
      
      if (onActionComplete) {
        onActionComplete();
      }
      
      handleClose();
    } catch (err) {
      console.error('Error approving leave request:', err);
      showError(getErrorMessage(err, 'Notification operation failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle work assignment for already-approved leaves (notify-only)
   */
  const handleAssignWork = async () => {
    // Validate user selection
    if (!selectedUserId) {
      showError('Please select a user to assign this work to');
      return;
    }
    
    setLoading(true);
    try {
      const leaveRequestId = notification.metadata?.leave_request_id;
      if (!leaveRequestId) {
        showError('Leave request ID not found');
        return;
      }

      await leaveService.assignLeaveWork(leaveRequestId, selectedUserId);
      
      // Mark notification as read (only if this is a real notification, not synthetic from admin dashboard)
      if (!notification.is_synthetic) {
        await notificationService.markAsRead(notification.id);
      }
      
      showSuccess('Work assigned successfully');
      
      // Call action complete callback
      if (onActionComplete) {
        onActionComplete();
      }
      
      handleClose();
    } catch (err) {
      console.error('Error assigning work:', err);
      showError(getErrorMessage(err, 'Notification operation failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Reject button click (show reason input and clear assignment)
   */
  const handleDenyClick = () => {
    // Clear assignment selection when rejecting
    setSelectedUserId(null);
    setSelectedUserName('');
    setUserSearchTerm('');
    setShowUserDropdown(false);
    setShowDenyReason(true);
  };

  /**
   * Handle Leave Request Rejection (Complete)
   */
  const handleCompleteDeny = async () => {
    if (!denyReason.trim()) {
      showError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    try {
      const leaveRequestId = notification.metadata?.leave_request_id;
      if (!leaveRequestId) {
        showError('Leave request ID not found');
        return;
      }

      await axiosInstance.post(`/api/leave/admin/requests/${leaveRequestId}/reject/`, {
        admin_notes: denyReason
      });
      
      // Mark notification as read (only if this is a real notification, not synthetic from admin dashboard)
      if (!notification.is_synthetic) {
        await notificationService.markAsRead(notification.id);
      }
      
      showSuccess('Leave request rejected');
      
      // Call action complete callback
      if (onActionComplete) {
        onActionComplete();
      }
      
      handleClose();
    } catch (err) {
      console.error('Error rejecting leave request:', err);
      showError(getErrorMessage(err, 'Notification operation failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filter available users by search term
   */
  const getFilteredUsers = () => {
    if (!userSearchTerm) {
      return availableUsers;
    }
    
    const searchLower = userSearchTerm.toLowerCase();
    return availableUsers.filter(user => 
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.department && user.department.toLowerCase().includes(searchLower))
    );
  };

  /**
   * Render work assignment dropdown (shown before leave request details)
   */
  const renderWorkAssignment = () => {
    if (notification.notification_type === 'leave_request' && shouldShowAssignment()) {
      const filteredUsers = getFilteredUsers();
      const metadata = freshMetadata || notification.metadata;
      let daysUntilStart = 0;
      if (metadata?.start_date) {
        const [y, m, d] = metadata.start_date.split('-').map(Number);
        const sd = new Date(y, m - 1, d);
        const td = new Date();
        td.setHours(0, 0, 0, 0);
        daysUntilStart = Math.floor((sd - td) / (1000 * 60 * 60 * 24));
      }
      const isMandatory = daysUntilStart < 2;
      
      return (
        <div className="hms-notif-modal-work-assignment-container">
          <h4 className="assignment-section-title">Assign Work Coverage</h4>
          
          {/* Work Assignment Section */}
          <div className="work-assignment-section">
            <p className="assignment-helper-text">
              <FontAwesomeIcon icon={faUserPlus} className="assignment-icon" />
              {isMandatory 
                ? <>Select a replacement (required -- leave starts within 2 days) <span className="required-indicator">*</span></>
                : 'Select a replacement now, or approve without one and a reminder will be sent 2 days before leave starts.'
              }
            </p>
            
            {loadingUsers ? (
              <div className="assignment-loading">Loading available users...</div>
            ) : usersError ? (
              <div className="assignment-error">{usersError}</div>
            ) : (
              <>
                {/* Custom Dropdown with Search */}
                <div className="assignment-dropdown-container" ref={userDropdownRef}>
                  <div className="assignment-search-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder={selectedUserName || "Search by name, email, or department..."}
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      onFocus={() => setShowUserDropdown(true)}
                      className="assignment-search-input"
                      disabled={loading}
                    />
                  </div>
                  
                  {/* Dropdown Results */}
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="assignment-dropdown-results">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className={`assignment-dropdown-item ${selectedUserId === user.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setSelectedUserName(user.name);
                            setUserSearchTerm('');
                            setShowUserDropdown(false);
                          }}
                        >
                          <div className="assignment-user-info">
                            <div className="assignment-user-name">
                              {user.name}
                              {user.has_pending_leave && (
                                <span 
                                  className="assignment-pending-badge"
                                  title={`Has pending leave request: ${user.pending_start_date} to ${user.pending_end_date}`}
                                >
                                  Pending
                                </span>
                              )}
                            </div>
                            <div className="assignment-user-details">
                              {user.department && <span>{user.department}</span>}
                              {user.department && <span> • </span>}
                              <span>{user.email}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected User Display */}
                  {selectedUserId && !showUserDropdown && (
                    <div className="assignment-selected-user">
                      <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      <span>{selectedUserName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUserId(null);
                          setSelectedUserName('');
                          setUserSearchTerm('');
                        }}
                        className="clear-selection-btn"
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                </div>
                
                {availableUsers.length === 0 && (
                  <div className="assignment-warning">
                    No users are currently available for assignment. All users may be on leave.
                  </div>
                )}
                
                {filteredUsers.length === 0 && availableUsers.length > 0 && (
                  <div className="assignment-info">
                    No users match your search. Try different keywords.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  /**
   * Generate 4-week timeline dates starting 1 day before leave request start
   */
  const generateTimelineDates = () => {
    const metadata = freshMetadata || notification.metadata;
    const startDate = metadata?.start_date;
    
    if (!startDate) return [];
    
    const dates = [];
    const requestStart = new Date(startDate);
    // Start 1 day before the request start date
    const timelineStart = new Date(requestStart);
    timelineStart.setDate(timelineStart.getDate() - 1);
    
    // Generate 28 days (4 weeks)
    for (let i = 0; i < 28; i++) {
      const date = new Date(timelineStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  /**
   * Check if a date is within the requesting employee's leave period
   */
  const isRequestingEmployeeLeaveDate = (date) => {
    const metadata = freshMetadata || notification.metadata;
    const startDate = metadata?.start_date;
    const endDate = metadata?.end_date;
    
    if (!startDate || !endDate) return false;
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const reqStart = new Date(startDate);
    reqStart.setHours(0, 0, 0, 0);
    
    const reqEnd = new Date(endDate);
    reqEnd.setHours(0, 0, 0, 0);
    
    return checkDate >= reqStart && checkDate <= reqEnd;
  };

  /**
   * Get users on leave for a specific date
   */
  const getUsersOnLeaveForDate = (date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    return usersOnLeave.filter(user => {
      const userStart = new Date(user.start_date);
      userStart.setHours(0, 0, 0, 0);
      
      const userEnd = new Date(user.end_date);
      userEnd.setHours(0, 0, 0, 0);
      
      return checkDate >= userStart && checkDate <= userEnd;
    });
  };

  /**
   * Render 4-week timeline calendar
   */
  const renderLeaveTimeline = () => {
    if (notification.notification_type === 'leave_request' && shouldShowAssignment()) {
      const timelineDates = generateTimelineDates();
      
      if (timelineDates.length === 0) return null;
      
      return (
        <div className="leave-timeline-container">
          <div className="leave-timeline-scroll">
            {timelineDates.map((date, index) => {
              const isRequestDate = isRequestingEmployeeLeaveDate(date);
              const usersOnThisDate = getUsersOnLeaveForDate(date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              
              // Format DD/MM for display
              const dayMonth = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
              
              // Build tooltip text
              let tooltipText = dayMonth;
              if (usersOnThisDate.length > 0) {
                tooltipText += '\n\n' + usersOnThisDate.map(u => `${u.name} - ${u.leave_type || 'Absent'}`).join('\n');
              }
              
              return (
                <div 
                  key={index} 
                  className={`leave-timeline-day ${isRequestDate ? 'requesting-employee' : ''} ${isWeekend ? 'weekend' : ''}`}
                  title={tooltipText}
                >
                  <div className="timeline-day-header">
                    <div className="timeline-day-full-date">{dayMonth}</div>
                    <div className="timeline-day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="timeline-day-date">{date.getDate()}</div>
                  </div>
                  
                  {usersOnThisDate.length > 0 && (
                    <div className="timeline-day-indicators">
                      {usersOnThisDate.slice(0, 3).map((user, idx) => (
                        <div 
                          key={idx}
                          className="timeline-leave-indicator"
                          style={{ backgroundColor: user.leave_type_color || '#6b7280' }}
                        />
                      ))}
                      {usersOnThisDate.length > 3 && (
                        <div className="timeline-more-indicator">+{usersOnThisDate.length - 3}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  /**
   * Render users on leave during the leave request period
   */
  const renderUsersOnLeave = () => {
    if (notification.notification_type === 'leave_request' && shouldShowAssignment()) {
      return (
        <div className="users-on-leave-container">
          <h4 className="users-on-leave-title">Users on Leave During This Period</h4>
          
          {loadingUsersOnLeave ? (
            <div className="users-on-leave-loading">Loading...</div>
          ) : usersOnLeave.length > 0 ? (
            <div className="users-on-leave-list">
              {usersOnLeave.map((user, index) => (
                <div key={`${user.id}-${index}`} className="user-on-leave-item">
                  <span className="user-on-leave-name">{user.name}</span>
                  <span 
                    className="user-on-leave-badge"
                    style={{
                      backgroundColor: user.leave_type_color || '#6b7280',
                      color: '#ffffff'
                    }}
                  >
                    {/* A colleague's leave arrives without its type (GDPR consent). */}
                    {user.leave_type || 'Absent'}
                  </span>
                  <span className="user-on-leave-dates">
                    {new Date(user.start_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                    {' - '}
                    {new Date(user.end_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="users-on-leave-empty">
              No other users on leave during this period
            </div>
          )}
          
          {/* 4-Week Timeline Calendar */}
          {renderLeaveTimeline()}
        </div>
      );
    }
    return null;
  };

  /**
   * Render action buttons based on notification type
   */
  const renderActions = () => {
    // For leave requests - check if it requires approval
    if (notification.notification_type === 'leave_request') {
      const metadata = freshMetadata || notification.metadata;
      const leaveTypeCode = metadata?.leave_type_code;
      const isHoOrTl = leaveTypeCode === 'HO' || leaveTypeCode === 'TL';
      const requiresApproval = shouldShowActions(); // True for pending requests that need approval
      
      // For HO/TL leave types that require approval but NO work assignment
      if (isHoOrTl && requiresApproval) {
        return (
          <div className="hms-notif-modal-actions-container">
            <h4 className="actions-title">Actions Required</h4>
            
            <div className="action-buttons">
              <button
                onClick={handleApproveLeave}
                disabled={loading}
                className="hms-notif-action-btn approve-btn"
                title="Approve leave request"
              >
                <FontAwesomeIcon icon={faCheck} />
                Approve
              </button>
              <button
                onClick={handleDenyClick}
                disabled={loading || showDenyReason}
                className="hms-notif-action-btn deny-btn"
                title="Reject leave request"
              >
                <FontAwesomeIcon icon={faTimes} />
                Reject
              </button>
            </div>
            
            {/* Rejection reason */}
            {showDenyReason && (
              <div className="deny-reason-container">
                <div className="deny-reason-header">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <h5>Provide Rejection Reason</h5>
                </div>
                <label htmlFor="deny-reason">Please explain why this leave request is being rejected:</label>
                <textarea
                  id="deny-reason"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="Enter detailed reason for rejection (this will be sent to the employee)..."
                  rows={4}
                  disabled={loading}
                  className="deny-reason-input"
                />
                <div className="deny-reason-actions">
                  <button
                    onClick={() => {
                      setShowDenyReason(false);
                      setDenyReason('');
                    }}
                    disabled={loading}
                    className="hms-notif-action-btn cancel-deny-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteDeny}
                    disabled={loading || !denyReason.trim()}
                    className="hms-notif-action-btn complete-deny-btn"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Confirm Rejection
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      }
      
      // For other leave types with assignment requirement
      if (shouldShowAssignment()) {
        const isNotifyOnly = metadata?.requires_approval === false || leaveRequestStatus === 'approved';

        let daysUntilStart = 0;
        if (metadata?.start_date) {
          const [sy, sm, sd] = metadata.start_date.split('-').map(Number);
          const startDate = new Date(sy, sm - 1, sd);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          daysUntilStart = Math.floor((startDate - today) / (1000 * 60 * 60 * 24));
        }
        const replacementMandatory = daysUntilStart < 2;
        
        return (
          <div className="hms-notif-modal-actions-container">
            <h4 className="actions-title">{isNotifyOnly ? 'Actions' : 'Actions Required'}</h4>
            
            {/* Action Buttons - Different for approval-required vs notify-only */}
            <div className="action-buttons">
              {requiresApproval ? (
                <>
                  {/* Approval-required: Show Approve & Reject buttons */}
                  <button
                    onClick={handleApproveLeave}
                    disabled={loading || (replacementMandatory && !selectedUserId) || loadingUsers}
                    className="hms-notif-action-btn approve-btn"
                    title={replacementMandatory && !selectedUserId ? "Leave starts within 2 days — replacement is required" : "Approve leave request"}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    {selectedUserId ? 'Approve & Assign' : 'Approve'}
                  </button>
                  <button
                    onClick={handleDenyClick}
                    disabled={loading || showDenyReason}
                    className="hms-notif-action-btn deny-btn"
                    title="Reject leave request"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Reject
                  </button>
                </>
              ) : (
                <>
                  {/* Notify-only: Show just Assign Work button */}
                  <button
                    onClick={handleAssignWork}
                    disabled={loading || !selectedUserId || loadingUsers}
                    className="hms-notif-action-btn approve-btn"
                    title={!selectedUserId ? "Please select a user to assign work to" : "Assign work coverage"}
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    Assign Work
                  </button>
                </>
              )}
            </div>
          
          {/* Rejection reason (only shown for approval-required leaves) */}
          {requiresApproval && showDenyReason && (
            <div className="deny-reason-container">
              <div className="deny-reason-header">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <h5>Provide Rejection Reason</h5>
              </div>
              <label htmlFor="deny-reason">Please explain why this leave request is being rejected:</label>
              <textarea
                id="deny-reason"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Enter detailed reason for rejection (this will be sent to the employee)..."
                rows={4}
                disabled={loading}
                className="deny-reason-input"
              />
              <div className="deny-reason-actions">
                <button
                  onClick={() => {
                    setShowDenyReason(false);
                    setDenyReason('');
                  }}
                  disabled={loading}
                  className="hms-notif-action-btn cancel-deny-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteDeny}
                  disabled={loading || !denyReason.trim()}
                  className="hms-notif-action-btn complete-deny-btn"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Confirm Rejection
                </button>
              </div>
            </div>
          )}
        </div>
      );
      }
    }

    // Future: Add more notification types here
    // Example:
    // case 'document_review':
    //   return <DocumentReviewActions />;
    // case 'approval_required':
    //   return <ApprovalActions />;

    // Default: No specific actions
    return null;
  };

  /**
   * Render work assignment info section (displayed after approval)
   */
  const renderWorkAssignmentInfo = () => {
    if (!workAssignment) return null;
    
    const assignedTo = workAssignment.assigned_to_details;
    const assignedBy = workAssignment.assigned_by_details;
    
    return (
      <div className="work-assignment-info-container">
        <h4 className="work-assignment-title">Work Coverage Assignment</h4>
        <div className="work-assignment-card">
          <div className="work-assignment-header">
            <FontAwesomeIcon icon={faUserPlus} className="work-assignment-icon" />
            <span>Workflow Takeover</span>
          </div>
          
          <div className="work-assignment-details">
            <div className="assignment-row">
              <span className="assignment-label">Assigned To:</span>
              <div className="assignment-user">
                {assignedTo.avatar_photo && (
                  <img 
                    src={getAbsoluteMediaUrl(assignedTo.avatar_photo)} 
                    alt={assignedTo.name}
                    className="assignment-user-avatar"
                  />
                )}
                <div className="assignment-user-info">
                  <span className="assignment-user-name">{assignedTo.name}</span>
                  <span className="assignment-user-email">{assignedTo.email}</span>
                </div>
              </div>
            </div>
            
            <div className="assignment-row">
              <span className="assignment-label">Assigned By:</span>
              <span className="assignment-value">{assignedBy.name}</span>
            </div>
            
            <div className="assignment-row">
              <span className="assignment-label">Assignment Date:</span>
              <span className="assignment-value">
                {new Date(workAssignment.assignment_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            {workAssignment.notes && (
              <div className="assignment-row assignment-notes-row">
                <span className="assignment-label">Notes:</span>
                <span className="assignment-value">{workAssignment.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Handle navigation to todo list with highlighting
   */
  const handleViewTodo = () => {
    const todoId = notification.metadata?.todo_id;
    if (todoId) {
      // Navigate to todos screen with state to highlight specific todo
      history.push('/settings/todos', { highlightTodoId: todoId });
      onClose(); // Close modal after navigation
    }
  };

  /**
   * Handle sick note download from notification
   */
  const handleDownloadSickNote = async () => {
    const meta = freshMetadata || notification.metadata;
    
    if (!meta?.sick_note_docuware_document_id || !meta?.sick_note_name) {
      console.error('Missing sick note document ID or name');
      return;
    }
    
    try {
      setDownloadingSickNote(true);
      await downloadSickNote(meta.sick_note_docuware_document_id, meta.sick_note_name);
    } catch (error) {
      console.error('Error downloading sick note:', error);
      // Could show an error message to the user here
    } finally {
      setDownloadingSickNote(false);
    }
  };

  /**
   * Render todo reminder metadata
   */
  const renderTodoMetadata = () => {
    if (notification.notification_type !== 'todo_reminder' || !notification.metadata) {
      return null;
    }

    const meta = notification.metadata;
    
    // Dynamically calculate if overdue and days overdue based on current date
    // This ensures the display is always accurate, even if the notification is old
    let isOverdue = false;
    let daysOverdue = 0;
    
    if (meta.todo_due_date) {
      const dueDate = new Date(meta.todo_due_date);
      const today = new Date();
      
      // Reset time to midnight for accurate day comparison
      dueDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      if (today > dueDate) {
        isOverdue = true;
        // Calculate days difference
        const timeDiff = today.getTime() - dueDate.getTime();
        daysOverdue = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      }
    }

    return (
      <div className="hms-notif-modal-todo-reminder-section">
        {/* Todo Title */}
        <h3 className="hms-notif-modal-todo-title">
          {meta.todo_title}
        </h3>

        {/* Overdue Warning */}
        {isOverdue && (
          <div className="hms-notif-modal-todo-expiry-indicator">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="hms-notif-modal-todo-expiry-text">
              OVERDUE by {daysOverdue} day{daysOverdue !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Todo Details Grid */}
        <div className="hms-notif-modal-todo-details-grid">
          <div className="hms-notif-modal-todo-detail-item">
            <span className="hms-notif-modal-todo-detail-label">Description</span>
            <span className={`hms-notif-modal-todo-detail-value ${meta.todo_description ? 'hms-notif-modal-todo-description-badge' : ''}`}>
              {meta.todo_description || 'No description'}
            </span>
          </div>

          <div className="hms-notif-modal-todo-detail-item">
            <span className="hms-notif-modal-todo-detail-label">Status</span>
            <span className="hms-notif-modal-todo-detail-value">
              {meta.todo_status_display}
            </span>
          </div>

          <div className="hms-notif-modal-todo-detail-item">
            <span className="hms-notif-modal-todo-detail-label">Priority</span>
            <span className={`hms-notif-modal-todo-detail-value ${meta.todo_priority === 'urgent' ? 'hms-notif-modal-todo-urgent-badge' : ''}`}>
              {meta.todo_priority_display}
            </span>
          </div>

          <div className="hms-notif-modal-todo-detail-item">
            <span className="hms-notif-modal-todo-detail-label">Due Date</span>
            <span className={`hms-notif-modal-todo-detail-value ${meta.todo_due_date ? 'hms-notif-modal-todo-duedate-badge' : ''}`}>
              {meta.todo_due_date || 'No due date set'}
            </span>
          </div>

          <div className="hms-notif-modal-todo-detail-item">
            <span className="hms-notif-modal-todo-detail-label">Reminder Type</span>
            <span className="hms-notif-modal-todo-detail-value">
              {meta.reminder_reason === 'expiry_approaching' && 'Daily (Expiry Soon)'}
              {meta.reminder_reason === 'urgent_no_expiry' && 'Every 3 Days (Urgent)'}
              {meta.reminder_reason === 'routine_reminder' && 'Weekly (Routine)'}
            </span>
          </div>
        </div>

        {/* Todo Actions */}
        <div className="hms-notif-modal-todo-actions-container">
          <h4 className="hms-notif-modal-todo-actions-title">Actions</h4>
          <div className="hms-notif-modal-todo-action-buttons">
            <button 
              className="hms-notif-modal-todo-action-btn-view"
              onClick={handleViewTodo}
            >
              <FontAwesomeIcon icon={faEye} />
              View Todo
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render leave request metadata
   */
  const renderLeaveMetadata = () => {
    if (notification.notification_type !== 'leave_request' || !notification.metadata) {
      return null;
    }

    // Use fresh metadata if available, otherwise use notification's metadata
    const meta = freshMetadata || notification.metadata;
    
    return (
      <>
        {/* Display Cancellation Message if applicable */}
        {meta.cancelled === true && (
          <div className="admin-notes-container" style={{backgroundColor: '#fff3cd', borderColor: '#ffc107'}}>
            <div className="admin-notes-header" style={{color: '#856404'}}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <h5>Request Cancelled by Employee</h5>
            </div>
            <div className="admin-notes-content">
              <span className="admin-notes-label" style={{color: '#856404'}}>
                This {meta.original_status === 'pending' ? 'pending' : 'approved'} leave request was cancelled by {meta.cancelled_by || 'the employee'}.
              </span>
              <p className="admin-notes-text" style={{color: '#856404'}}>
                No action is required. The cancellation has been recorded.
              </p>
            </div>
          </div>
        )}
        
        {/* Display Auto-Approved Badge if applicable */}
        {meta.requires_approval === false && meta.auto_approved && !meta.cancelled && (
          <div className="admin-notes-container approved">
            <div className="admin-notes-header">
              <FontAwesomeIcon icon={faCheck} />
              <h5>Auto-Approved Leave</h5>
            </div>
            <div className="admin-notes-content">
              <span className="admin-notes-label">
                This leave type does not require approval and has been automatically approved.
              </span>
              <p className="admin-notes-text">No action required - this is for your information only.</p>
            </div>
          </div>
        )}
        
        {/* Display Admin Notes (Rejection/Approval Reason) if present - SHOWN FIRST */}
        {meta.admin_notes && !meta.auto_approved && !meta.cancelled && (
          <div className={`admin-notes-container ${meta.approval_action === 'rejected' ? 'rejected' : 'approved'}`}>
            <div className="admin-notes-header">
              <FontAwesomeIcon icon={meta.approval_action === 'rejected' ? faExclamationTriangle : faCheck} />
              <h5>{meta.approval_action === 'rejected' ? 'Rejection Reason' : 'Approval Notes'}</h5>
            </div>
            <div className="admin-notes-content">
              <span className="admin-notes-label">
                {meta.approval_action === 'rejected' ? 'This request was rejected for the following reason:' : 'Approval notes from supervisor:'}
              </span>
              <p className="admin-notes-text">{meta.admin_notes}</p>
              {meta.reviewed_by_name && (
                <span className="admin-notes-reviewer">— {meta.reviewed_by_name}</span>
              )}
            </div>
          </div>
        )}
        
        <div className="hms-notif-modal-metadata">
          <h4>Leave Request Details</h4>
          <div className="metadata-grid">
            {meta.employee_name && (
              <div className="metadata-item">
                <span className="metadata-label">Employee:</span>
                <span className="metadata-value">{meta.employee_name}</span>
              </div>
            )}
            {meta.leave_type && (
              <div className="metadata-item">
                <span className="metadata-label">Leave Type:</span>
                <span className="metadata-value">{meta.leave_type}</span>
              </div>
            )}
            {meta.start_date && meta.end_date && (
              <div className="metadata-item">
                <span className="metadata-label">Dates:</span>
                <span className="metadata-value">
                  {new Date(meta.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '}-{' '}
                  {new Date(meta.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}
            {meta.total_days && (
              <div className="metadata-item">
                <span className="metadata-label">Total Days:</span>
                <span className="metadata-value">{meta.total_days} days</span>
              </div>
            )}
            {meta.reason && (
              <div className="metadata-item full-width">
                <span className="metadata-label">Employee Reason:</span>
                <span className="metadata-value">{meta.reason}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Sick Note Display Section - For Sick Leave >= 3 consecutive days */}
        {meta.leave_type === 'Sick Leave' && meta.consecutive_sick_days >= 3 && (
          <div className="sick-note-display-section">
            <div className="detail-label">Doctor's Sick Note</div>
            {meta.sick_note_id ? (
              <div className="sick-note-card">
                <div className="sick-note-card-header">
                  <i className="fas fa-file-medical" style={{marginRight: '8px', color: '#4CAF50'}}></i>
                  <span className="sick-note-card-title">Medical Certificate Attached</span>
                </div>
                <div className="sick-note-card-body">
                  <div className="sick-note-info-row">
                    <span className="sick-note-info-label">File:</span>
                    <span className="sick-note-info-value" title={meta.sick_note_name || 'Sick note file'}>
                      {meta.sick_note_name || 'sick_note.pdf'}
                    </span>
                  </div>
                  {meta.sick_note_uploaded_at && (
                    <div className="sick-note-info-row">
                      <span className="sick-note-info-label">Uploaded:</span>
                      <span className="sick-note-info-value">
                        {new Date(meta.sick_note_uploaded_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                  <div className="sick-note-info-row">
                    <span className="sick-note-info-label">Consecutive Sick Days:</span>
                    <span className="sick-note-info-value">{meta.consecutive_sick_days} days</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="sick-note-download-btn"
                  onClick={handleDownloadSickNote}
                  disabled={downloadingSickNote}
                >
                  <i className="fas fa-download" style={{marginRight: '6px'}}></i>
                  {downloadingSickNote ? 'Downloading...' : 'Download Sick Note'}
                </button>
              </div>
            ) : (
              <div className="sick-note-card sick-note-missing">
                <div className="sick-note-card-header">
                  <i className="fas fa-exclamation-triangle" style={{marginRight: '8px', color: '#ff9800'}}></i>
                  <span className="sick-note-card-title">Sick Note Required</span>
                </div>
                <div className="sick-note-card-body">
                  <div className="sick-note-info-row">
                    <span className="sick-note-info-label">Status:</span>
                    <span className="sick-note-info-value">Not yet uploaded</span>
                  </div>
                  <div className="sick-note-info-row">
                    <span className="sick-note-info-label">Consecutive Sick Days:</span>
                    <span className="sick-note-info-value">{meta.consecutive_sick_days} days</span>
                  </div>
                  <p className="sick-note-warning-text">
                    A doctor's sick note is required for {Math.floor(meta.consecutive_sick_days)} or more consecutive sick days.
                    The employee should upload the medical certificate.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // Route CRM RFC notifications to specialized modal (both request and reviewed)
  if (notification?.notification_type === 'crm_rfc_request' || notification?.notification_type === 'crm_rfc_reviewed') {
    return (
      <CRMRFCNotificationModal 
        isOpen={isOpen}
        onClose={onClose}
        notification={notification}
      />
    );
  }

  // Don't render if not open or no notification
  if (!isOpen || !notification) {
    return null;
  }

  return (
    <div 
      className="hms-notif-modal-overlay" 
      onClick={handleOverlayClick}
    >
      <div className="hms-notif-modal">
        {/* Header */}
        <div className="hms-notif-modal-header">
          <div className="header-content">
            <h3 className="modal-title">{notification.title}</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="hms-notif-modal-close-btn"
            title="Close"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="hms-notif-modal-body">
          {/* Type and Date Badges */}
          <div className="hms-notif-modal-badges">
            <div className="badge type-badge">
              <span className="badge-label">Type:</span>
              <span className="badge-value">{getNotificationTypeDisplay()}</span>
            </div>
            <div className="badge date-badge">
              <span className="badge-label">Date:</span>
              <span className="badge-value">{formatDate(notification.created_at)}</span>
            </div>
            {/* Leave Request Status Badge - Show for leave requests with definitive status */}
            {notification.notification_type === 'leave_request' && leaveRequestStatus && (
              <div className={`badge status-badge-prominent ${
                leaveRequestStatus === 'approved' ? 'status-approved' :
                leaveRequestStatus === 'rejected' ? 'status-rejected' :
                leaveRequestStatus === 'cancelled' ? 'status-cancelled' :
                leaveRequestStatus === 'pending' ? 'status-pending' :
                'status-default'
              }`}>
                <span className="badge-label">Status:</span>
                <span className="badge-value">
                  {leaveRequestStatus === 'approved' && 'Approved'}
                  {leaveRequestStatus === 'rejected' && 'Rejected'}
                  {leaveRequestStatus === 'cancelled' && 'Cancelled'}
                  {leaveRequestStatus === 'pending' && 'Pending'}
                  {!['approved', 'rejected', 'cancelled', 'pending'].includes(leaveRequestStatus) && leaveRequestStatus}
                </span>
              </div>
            )}
          </div>

          {/* Description - Only show if no admin notes, otherwise show admin notes prominently */}
          {/* Hide description for todo_reminder as it's shown in the todo details section */}
          {/* For financial notifications, show cleaned description (without redundant metadata) */}
          {!notification.metadata?.admin_notes && notification.notification_type !== 'todo_reminder' && (
            <div className="hms-notif-modal-description">
              <p>
                {(notification.notification_type === 'financial_approval_request' ||
                  notification.notification_type === 'financial_approval_approved' ||
                  notification.notification_type === 'financial_approval_rejected' ||
                  notification.notification_type === 'invoice_approved' ||
                  notification.notification_type === 'invoice_rejected' ||
                  notification.notification_type === 'conversion_approval_request' ||
                  notification.notification_type === 'conversion_approved' ||
                  notification.notification_type === 'conversion_rejected')
                  ? cleanFinancialDescription(notification.description)
                  : notification.description
                }
              </p>
            </div>
          )}
          
          {/* Financial Approval & Conversion Approval Invoice Details */}
          {(notification.notification_type === 'financial_approval_request' || 
            notification.notification_type === 'financial_approval_approved' || 
            notification.notification_type === 'financial_approval_rejected' ||
            notification.notification_type === 'invoice_approved' ||
            notification.notification_type === 'invoice_rejected' ||
            notification.notification_type === 'conversion_approval_request' ||
            notification.notification_type === 'conversion_approved' ||
            notification.notification_type === 'conversion_rejected') && (
            <div className="hms-notif-modal-metadata">
              <h4>Invoice Details</h4>
              <div className="metadata-grid">
                {notification.metadata?.invoice_number && (
                  <div className="metadata-item">
                    <span className="metadata-label">Invoice Number:</span>
                    <span className="metadata-value">{notification.metadata.invoice_number}</span>
                  </div>
                )}
                {notification.metadata?.partner_name && (
                  <div className="metadata-item">
                    <span className="metadata-label">Partner:</span>
                    <span className="metadata-value">{notification.metadata.partner_name}</span>
                  </div>
                )}
                {notification.metadata?.gross_amount && notification.metadata?.currency && (
                  <div className="metadata-item">
                    <span className="metadata-label">Amount:</span>
                    <span className="metadata-value" style={{ fontWeight: '700', color: '#4a6b25' }}>
                      {notification.metadata.currency} {parseFloat(notification.metadata.gross_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {notification.metadata?.submitted_by_name && 
                 (notification.notification_type === 'financial_approval_request' || 
                  notification.notification_type === 'conversion_approval_request') && (
                  <div className="metadata-item">
                    <span className="metadata-label">Submitted By:</span>
                    <span className="metadata-value">{notification.metadata.submitted_by_name}</span>
                  </div>
                )}
                {notification.metadata?.approved_by_name && 
                 (notification.notification_type === 'financial_approval_approved' || 
                  notification.notification_type === 'invoice_approved' ||
                  notification.notification_type === 'conversion_approved') && (
                  <div className="metadata-item">
                    <span className="metadata-label">Approved By:</span>
                    <span className="metadata-value">{notification.metadata.approved_by_name}</span>
                  </div>
                )}
                {notification.metadata?.rejected_by_name && 
                 (notification.notification_type === 'financial_approval_rejected' || 
                  notification.notification_type === 'invoice_rejected' ||
                  notification.notification_type === 'conversion_rejected') && (
                  <div className="metadata-item">
                    <span className="metadata-label">Rejected By:</span>
                    <span className="metadata-value">{notification.metadata.rejected_by_name}</span>
                  </div>
                )}
                {notification.metadata?.rejection_reason && 
                 (notification.notification_type === 'financial_approval_rejected' || 
                  notification.notification_type === 'invoice_rejected' ||
                  notification.notification_type === 'conversion_rejected') && (
                  <div className="metadata-item full-width">
                    <span className="metadata-label">Rejection Reason:</span>
                    <span className="metadata-value" style={{ color: '#dc2626', fontWeight: '600' }}>
                      {notification.metadata.rejection_reason}
                    </span>
                  </div>
                )}
                {notification.metadata?.approval_notes && 
                 (notification.notification_type === 'financial_approval_approved' || 
                  notification.notification_type === 'invoice_approved' ||
                  notification.notification_type === 'conversion_approved') && (
                  <div className="metadata-item full-width">
                    <span className="metadata-label">Approval Notes:</span>
                    <span className="metadata-value">{notification.metadata.approval_notes}</span>
                  </div>
                )}
                {notification.metadata?.outgoing_number && notification.notification_type === 'conversion_approved' && (
                  <div className="metadata-item">
                    <span className="metadata-label">Outgoing Invoice:</span>
                    <span className="metadata-value" style={{ fontWeight: '700', color: '#4a6b25' }}>
                      {notification.metadata.outgoing_number}
                    </span>
                  </div>
                )}
              </div>
              
              {/* View Invoice Button */}
              {(notification.metadata?.invoice_id || notification.metadata?.outgoing_id) && notification.action_url && (
                <div style={{ marginTop: '16px' }}>
                  <button
                    className="action-button primary"
                    onClick={() => {
                      // For conversion_approved, use outgoing_id to navigate to the new outgoing invoice
                      // For other financial notifications, use the action_url
                      let editUrl;
                      
                      if (notification.notification_type === 'conversion_approved' && notification.metadata?.outgoing_id) {
                        // Navigate to the newly created outgoing invoice
                        editUrl = `/invoicing/outgoing/edit/${notification.metadata.outgoing_id}`;
                      } else {
                        // Use action_url from notification which correctly differentiates between
                        // /invoicing/outgoing/{id} and /invoicing/proforma/{id}
                        // Convert view URL to edit URL format
                        const actionUrl = notification.action_url;
                        
                        if (actionUrl.includes('/invoicing/proforma/')) {
                          // Pro forma invoice: /invoicing/proforma/{id} -> /invoicing/proforma/edit/{id}
                          const invoiceId = actionUrl.split('/').pop();
                          editUrl = `/invoicing/proforma/edit/${invoiceId}`;
                        } else if (actionUrl.includes('/invoicing/outgoing/')) {
                          // Outgoing invoice: /invoicing/outgoing/{id} -> /invoicing/outgoing/edit/{id}
                          const invoiceId = actionUrl.split('/').pop();
                          editUrl = `/invoicing/outgoing/edit/${invoiceId}`;
                        } else {
                          // Fallback to action_url if format doesn't match expectations
                          editUrl = actionUrl;
                        }
                      }
                      
                      history.push(editUrl);
                      onClose();
                    }}
                    style={{ width: '100%' }}
                  >
                    View Invoice
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Delivery Review Notification Details */}
          {(notification.notification_type === 'delivery_review_request' || 
            notification.notification_type === 'delivery_comment_received') && (
            <div className="hms-notif-modal-metadata">
              <h4>Delivery Details</h4>
              <div className="metadata-grid">
                {notification.metadata?.delivery_number && (
                  <div className="metadata-item">
                    <span className="metadata-label">Delivery Number:</span>
                    <span className="metadata-value">{notification.metadata.delivery_number}</span>
                  </div>
                )}
                {notification.metadata?.vessel_name && (
                  <div className="metadata-item">
                    <span className="metadata-label">
                      <FontAwesomeIcon icon={faShip} style={{ marginRight: '6px' }} />
                      Vessel:
                    </span>
                    <span className="metadata-value">{notification.metadata.vessel_name}</span>
                  </div>
                )}
                {/* Purchase Products */}
                {notification.metadata?.purchase_products && notification.metadata.purchase_products.length > 0 && (
                  <div className="metadata-item full-width">
                    <span className="metadata-label">
                      <FontAwesomeIcon icon={faBoxOpen} style={{ marginRight: '6px', color: '#28a745' }} />
                      Purchase Products:
                    </span>
                    <span className="metadata-value">
                      {notification.metadata.purchase_products.map((product, index) => (
                        <span key={index} className="product-tag purchase-product">{product}</span>
                      ))}
                    </span>
                  </div>
                )}
                {/* Sales Products */}
                {notification.metadata?.sales_products && notification.metadata.sales_products.length > 0 && (
                  <div className="metadata-item full-width">
                    <span className="metadata-label">
                      <FontAwesomeIcon icon={faBoxOpen} style={{ marginRight: '6px', color: '#007bff' }} />
                      Sales Products:
                    </span>
                    <span className="metadata-value">
                      {notification.metadata.sales_products.map((product, index) => (
                        <span key={index} className="product-tag sales-product">{product}</span>
                      ))}
                    </span>
                  </div>
                )}
                {/* Fallback to product field if no purchase/sales products */}
                  {(!notification.metadata?.purchase_products || notification.metadata.purchase_products.length === 0) &&
                   (!notification.metadata?.sales_products || notification.metadata.sales_products.length === 0) &&
                   notification.metadata?.product && notification.metadata.product !== 'N/A' && (
                    <div className="metadata-item full-width">
                      <span className="metadata-label">
                        <FontAwesomeIcon icon={faBoxOpen} style={{ marginRight: '6px' }} />
                        Product:
                      </span>
                      <span className="metadata-value">{notification.metadata.product}</span>
                    </div>
                  )}
                {notification.metadata?.buyer_name && (
                  <div className="metadata-item">
                    <span className="metadata-label">Buyer:</span>
                    <span className="metadata-value">{notification.metadata.buyer_name}</span>
                  </div>
                )}
                {notification.metadata?.seller_name && (
                  <div className="metadata-item">
                    <span className="metadata-label">Seller:</span>
                    <span className="metadata-value">{notification.metadata.seller_name}</span>
                  </div>
                )}
                {notification.metadata?.submitted_by_name && 
                 notification.notification_type === 'delivery_review_request' && (
                  <div className="metadata-item">
                    <span className="metadata-label">Submitted By:</span>
                    <span className="metadata-value">{notification.metadata.submitted_by_name}</span>
                  </div>
                )}
                {notification.metadata?.commented_by_name && 
                 notification.notification_type === 'delivery_comment_received' && (
                  <div className="metadata-item">
                    <span className="metadata-label">Comment By:</span>
                    <span className="metadata-value">{notification.metadata.commented_by_name}</span>
                  </div>
                )}
                {(notification.metadata?.comment_text || notification.metadata?.comment_preview) && 
                 notification.notification_type === 'delivery_comment_received' && (
                  <div className="metadata-item full-width delivery-comment-display">
                    <span className="metadata-label">Comment:</span>
                    <span className="metadata-value delivery-comment-text">
                      {notification.metadata.comment_text || notification.metadata.comment_preview}
                    </span>
                  </div>
                )}
              </div>
              
              {/* View Delivery Button */}
              {notification.metadata?.delivery_id && (
                <div style={{ marginTop: '16px' }}>
                  <button
                    className="action-button primary"
                    onClick={() => {
                      history.push(`/deliveries/${notification.metadata.delivery_id}`);
                      onClose();
                    }}
                    style={{ width: '100%' }}
                  >
                    <FontAwesomeIcon icon={faTruck} style={{ marginRight: '8px' }} />
                    View Delivery
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Operations Access / Fuels Enquiry Notification Details */}
          {(notification.notification_type === 'ops_deal_created' || 
            notification.notification_type === 'ops_recap_created' ||
            notification.notification_type === 'ops_delivery_created') && (() => {
            const meta = notification.metadata || {};
            const isFuels = meta.commodity_type === 'fuels';
            const fuelsEvent = meta.event;

            let heading;
            if (isFuels) {
              if (fuelsEvent === 'stage_advance') heading = 'Bunker Enquiry Stage Advanced';
              else if (fuelsEvent === 'stage_rollback') heading = 'Bunker Enquiry Stage Rolled Back';
              else if (fuelsEvent === 'terminal_stage') heading = 'Bunker Enquiry Closed';
              else heading = 'New Bunker Enquiry';
            } else if (notification.notification_type === 'ops_recap_created') {
              heading = 'New Recap Details';
            } else if (notification.notification_type === 'ops_delivery_created') {
              heading = 'New Delivery Details';
            } else {
              heading = 'New Deal Details';
            }

            return (
              <div className="hms-notif-modal-metadata">
                <h4>
                  <FontAwesomeIcon icon={faCogs} style={{ marginRight: '8px' }} />
                  {heading}
                </h4>
                <div className="metadata-grid">
                  {meta.entity_number && (
                    <div className="metadata-item">
                      <span className="metadata-label">Reference Number:</span>
                      <span className="metadata-value">{meta.entity_number}</span>
                    </div>
                  )}
                  {meta.entity_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">{isFuels ? 'Customer:' : 'Name:'}</span>
                      <span className="metadata-value">{meta.entity_name}</span>
                    </div>
                  )}
                  {meta.recap_type && (
                    <div className="metadata-item">
                      <span className="metadata-label">Recap Type:</span>
                      <span className="metadata-value">{meta.recap_type}</span>
                    </div>
                  )}
                  {meta.vessel_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">
                        <FontAwesomeIcon icon={faShip} style={{ marginRight: '6px' }} />
                        Vessel:
                      </span>
                      <span className="metadata-value">
                        {meta.vessel_name}
                        {meta.imo_number ? ` (IMO ${meta.imo_number})` : ''}
                      </span>
                    </div>
                  )}
                  {isFuels && meta.port_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">Port:</span>
                      <span className="metadata-value">{meta.port_name}</span>
                    </div>
                  )}
                  {isFuels && meta.fuel_contract_shorthand && (
                    <div className="metadata-item">
                      <span className="metadata-label">Bunker Spec:</span>
                      <span className="metadata-value">{meta.fuel_contract_shorthand}</span>
                    </div>
                  )}
                  {isFuels && meta.credit_request && (
                    <div className="metadata-item">
                      <span className="metadata-label">Credit Request:</span>
                      <span className="metadata-value">
                        ${Number(meta.credit_request).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {isFuels && meta.from_stage_label && meta.to_stage_label && (
                    <div className="metadata-item">
                      <span className="metadata-label">Stage:</span>
                      <span className="metadata-value">
                        {meta.from_stage_label} → {meta.to_stage_label}
                      </span>
                    </div>
                  )}
                  {isFuels && fuelsEvent === 'terminal_stage' && meta.terminal_stage_label && (
                    <div className="metadata-item">
                      <span className="metadata-label">Final Stage:</span>
                      <span className="metadata-value">{meta.terminal_stage_label}</span>
                    </div>
                  )}
                  {isFuels && meta.branch_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">Branch:</span>
                      <span className="metadata-value">{meta.branch_name}</span>
                    </div>
                  )}
                  {meta.actor_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">Actioned By:</span>
                      <span className="metadata-value">{meta.actor_name}</span>
                    </div>
                  )}
                  {meta.creator_name && !meta.actor_name && (
                    <div className="metadata-item">
                      <span className="metadata-label">Created By:</span>
                      <span className="metadata-value">{meta.creator_name}</span>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '16px' }}>
                  {isFuels ? (
                    <button
                      className="action-button primary"
                      onClick={() => {
                        const target = notification.action_url
                          || (meta.fuel_enquiry_id
                            ? `/dashboard/trades/fuels/fuel-enquiries/${meta.fuel_enquiry_id}`
                            : '/dashboard/trades/fuels');
                        history.push(target);
                        onClose();
                      }}
                      style={{ width: '100%' }}
                    >
                      <FontAwesomeIcon icon={faShip} style={{ marginRight: '8px' }} />
                      Open Enquiry
                    </button>
                  ) : (
                    <button
                      className="action-button primary"
                      onClick={() => {
                        const entityType = meta.entity_type;
                        const entityId = meta.entity_id;
                        const commodityType = meta.commodity_type || 'solids';
                        const params = entityType && entityId
                          ? `?entity_type=${entityType}&entity_id=${entityId}`
                          : '';
                        const target = notification.action_url
                          ? `${notification.action_url}${params}`
                          : `/dashboard/trades/${commodityType}/operations-access/create${params}`;
                        history.push(target);
                        onClose();
                      }}
                      style={{ width: '100%' }}
                    >
                      <FontAwesomeIcon icon={faCogs} style={{ marginRight: '8px' }} />
                      Create Access Grouping
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Tonnage Approval notifications: surface a one-click jump
              to the underlying FuelEnquiry / TradeRecap. The backend
              already populates ``action_url`` in
              ``notifications/helpers.py::create_tonnage_approval_*``,
              so we just need to render a button when the notification
              type is one of the tonnage approval events. Covers the
              request (sent to approvers) and the decision events
              (approved / rejected, sent back to the requester) so
              both sides get a one-click path to the row. */}
          {(notification.notification_type === 'tonnage_approval_request'
            || notification.notification_type === 'tonnage_approval_approved'
            || notification.notification_type === 'tonnage_approval_rejected'
          ) && notification.action_url && (
            <div style={{ marginTop: '16px' }}>
              <button
                className="action-button primary"
                onClick={() => {
                  history.push(notification.action_url);
                  onClose();
                }}
                style={{ width: '100%' }}
              >
                <FontAwesomeIcon icon={faShip} style={{ marginRight: '8px' }} />
                {notification.notification_type === 'tonnage_approval_request'
                  ? 'Open Enquiry to Review'
                  : 'Open Enquiry'}
              </button>
            </div>
          )}

          {/* Contract sent to Legal (Legal opens the contract) or reviewed by
              Legal (the sender opens the recap): both carry
              ``metadata.legal_contract_id`` and an ``action_url``. */}
          {notification.metadata?.legal_contract_id && notification.action_url && (
            <div style={{ marginTop: '16px' }}>
              <button
                className="action-button primary"
                onClick={() => {
                  history.push(notification.action_url);
                  onClose();
                }}
                style={{ width: '100%' }}
              >
                <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: '8px' }} />
                {notification.action_url.startsWith('/trade-recap/') ? 'Open Recap' : 'Open Contract'}
              </button>
            </div>
          )}

          {/* Trade-document dispatch notifications (Phase 7): a Customer
              Confirmation or Supplier Nomination needs to be sent for a
              recapped fuel enquiry. The notification carries a generic FK to
              the dispatch row plus ``metadata.dispatch_id`` /
              ``metadata.fuel_enquiry_id`` and an ``action_url`` to the
              enquiry. We surface the document + recipient details and a
              Review & Send button that opens the enquiry with the dispatch id
              in route state -- the FuelEnquiryForm reads ``openDispatchId``
              and auto-opens the Review & Send modal for that row. If the
              dispatch id is missing we still navigate to the enquiry, where
              the outstanding-sends indicator lists the pending sends. */}
          {(notification.notification_type === 'customer_confirmation_required'
            || notification.notification_type === 'supplier_nomination_required'
          ) && (() => {
            const meta = freshMetadata || notification.metadata || {};
            const dispatchId = meta.dispatch_id || null;
            const enquiryId = meta.fuel_enquiry_id || null;
            const target = notification.action_url
              || (enquiryId
                ? `/dashboard/trades/fuels/fuel-enquiries/${enquiryId}`
                : null);

            return (
              <div className="hms-notif-modal-metadata">
                <h4>
                  <FontAwesomeIcon icon={faShip} style={{ marginRight: '8px' }} />
                  Document to Send
                </h4>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">Document:</span>
                    <span className="metadata-value">
                      {notification.notification_type === 'supplier_nomination_required'
                        ? `Supplier Nomination${meta.supplier_name ? ` — ${meta.supplier_name}` : ''}`
                        : 'Customer Confirmation'}
                    </span>
                  </div>
                  {meta.display_id && (
                    <div className="metadata-item">
                      <span className="metadata-label">Enquiry:</span>
                      <span className="metadata-value">{meta.display_id}</span>
                    </div>
                  )}
                </div>

                {target && (
                  <div style={{ marginTop: '16px' }}>
                    <button
                      className="action-button primary"
                      onClick={() => {
                        history.push(target, dispatchId ? { openDispatchId: dispatchId } : undefined);
                        onClose();
                      }}
                      style={{ width: '100%' }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '8px' }} />
                      Review &amp; Send
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Show action status message if action was already taken */}
          {(() => {
            // Use fresh metadata if available, otherwise use notification's metadata
            const metadata = freshMetadata || notification.metadata;

            // Check if notification has been superseded (e.g., leave edited)
            if (metadata?.superseded === true) {
              return (
                <div className="no-actions-message superseded-status">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>
                    This leave request has been modified. Please review the updated request.
                    {metadata?.edit_sequence_number && ` (Edit #${metadata.edit_sequence_number})`}
                  </span>
                </div>
              );
            }
            
            // Check if this was originally an actionable notification
            const wasActionRequired = notification.action_required || freshActionRequired;
            
            // Show status message if:
            // 1. Actions should not be shown anymore (already handled)
            // 2. It was an actionable notification originally
            // 3. We have a definitive status (approved/rejected)
            // 4. Status is not cancelled
            const shouldShowStatusMessage = !shouldShowActions() && 
                                            wasActionRequired && 
                                            leaveRequestStatus && 
                                            leaveRequestStatus !== 'cancelled' &&
                                            (leaveRequestStatus === 'approved' || leaveRequestStatus === 'rejected');
            
            if (!shouldShowStatusMessage) return null;
            
            return (
              <div className={`no-actions-message ${leaveRequestStatus === 'rejected' ? 'rejected-status' : 'approved-status'}`}>
                <FontAwesomeIcon icon={leaveRequestStatus === 'rejected' ? faTimes : faCheck} />
                <span>
                  {leaveRequestStatus === 'rejected' 
                    ? `Leave request rejected${leaveRequestAdminNotes ? `: ${leaveRequestAdminNotes}` : ''}`
                    : 'Leave request approved'
                  }
                </span>
              </div>
            );
          })()}

          {/* Work Assignment (shown before leave request details) */}
          {renderWorkAssignment()}

          {/* Users on Leave (shown after work assignment, before actions) */}
          {renderUsersOnLeave()}

          {/* Actions */}
          {renderActions()}

          {/* Metadata (for leave requests) */}
          {renderLeaveMetadata()}
          
          {/* Metadata (for todo reminders) */}
          {renderTodoMetadata()}
          
          {/* Work Assignment Info (for approved/completed leave requests) */}
          {renderWorkAssignmentInfo()}
        </div>

        {/* Footer */}
        <div className="hms-notif-modal-footer">
          <button
            onClick={handleClose}
            disabled={loading}
            className="hms-notif-modal-close-footer-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default HMSNotificationModal;

